import useScrollReveal from '../useScrollReveal';
import useSplitText from '../useSplitText';
import { sectionStyles, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const DEFAULT_PATH1 = {
    title: 'Para Empresas',
    description: 'Soluciones completas adaptadas a equipos y organizaciones.',
    features: ['Gestión de equipos', 'Soporte prioritario', 'Panel de administración'],
    cta_text: 'Empezar ahora',
    cta_link: '#contact',
    icon: '🏢',
};

const DEFAULT_PATH2 = {
    title: 'Para Individuos',
    description: 'Todo lo que necesitas para crecer de forma personal.',
    features: ['Acceso inmediato', 'Recursos ilimitados', 'Comunidad activa'],
    cta_text: 'Probar gratis',
    cta_link: '#contact',
    icon: '👤',
};

function CheckIcon({ color }) {
    return (
        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
    );
}

export default function SplitCtaSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const splitRef = useSplitText({ type: 'words', animation: 'fade-up', stagger: 0.04, disabled: !enableAnimations });
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'cards';

    const accent = s.accent || primaryColor || '#6366f1';
    const secondary = s.secondary || secondaryColor || '#ec4899';

    const path1 = { ...DEFAULT_PATH1, ...(content.path1 || {}) };
    const path2 = { ...DEFAULT_PATH2, ...(content.path2 || {}) };

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // ── Cards: two equal cards ──
    if (variant === 'cards') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                        {/* Path 1 — Accent */}
                        <div
                            className="relative flex flex-col rounded-2xl p-7 md:p-8 overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl"
                            style={{
                                background: `linear-gradient(135deg, ${accent}, ${hexToRgba(accent, 0.8)})`,
                                opacity: visible ? 1 : 0,
                                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                                transition: 'opacity 0.6s ease, transform 0.6s ease, box-shadow 0.3s ease',
                            }}
                        >
                            {/* Background glow orb */}
                            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-20" style={{ backgroundColor: '#ffffff', filter: 'blur(40px)' }} />
                            {path1.icon && (
                                <span className="text-4xl mb-4 block">{path1.icon}</span>
                            )}
                            <h3 ref={splitRef} className="text-2xl font-black text-white mb-2">{path1.title}</h3>
                            {path1.description && (
                                <p className="text-white/80 text-sm mb-6 leading-relaxed flex-1">{path1.description}</p>
                            )}
                            {path1.features && path1.features.length > 0 && (
                                <ul className="space-y-2 mb-8">
                                    {path1.features.map((feat, i) => (
                                        <li key={i} className="flex items-center gap-2 text-sm text-white/90">
                                            <CheckIcon color="rgba(255,255,255,0.9)" />
                                            {feat}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {path1.cta_text && (
                                <a
                                    {...resolveLinkProps(path1.cta_link)}
                                    className="mt-auto inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-300 hover:opacity-90"
                                    style={{ backgroundColor: '#ffffff', color: accent }}
                                >
                                    {path1.cta_text}
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </a>
                            )}
                        </div>

                        {/* Path 2 — Outline / secondary */}
                        <div
                            className="relative flex flex-col rounded-2xl p-7 md:p-8 overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-xl"
                            style={{
                                backgroundColor: s.dark ? 'rgba(255,255,255,0.04)' : '#ffffff',
                                border: `2px solid ${hexToRgba(secondary, 0.35)}`,
                                opacity: visible ? 1 : 0,
                                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                                transition: 'opacity 0.6s ease 120ms, transform 0.6s ease 120ms, box-shadow 0.3s ease',
                            }}
                        >
                            {path2.icon && (
                                <span className="text-4xl mb-4 block">{path2.icon}</span>
                            )}
                            <h3 className="text-2xl font-black mb-2" style={{ color: s.text }}>{path2.title}</h3>
                            {path2.description && (
                                <p className="text-sm mb-6 leading-relaxed flex-1" style={{ color: s.textSub }}>{path2.description}</p>
                            )}
                            {path2.features && path2.features.length > 0 && (
                                <ul className="space-y-2 mb-8">
                                    {path2.features.map((feat, i) => (
                                        <li key={i} className="flex items-center gap-2 text-sm" style={{ color: s.text }}>
                                            <CheckIcon color={secondary} />
                                            {feat}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {path2.cta_text && (
                                <a
                                    {...resolveLinkProps(path2.cta_link)}
                                    className="mt-auto inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-sm border-2 transition-all duration-300 hover:opacity-90"
                                    style={{ borderColor: secondary, color: secondary, backgroundColor: hexToRgba(secondary, 0.06) }}
                                >
                                    {path2.cta_text}
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Featured: path1 larger, path2 smaller ──
    if (variant === 'featured') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 items-stretch">
                        {/* Path 1 — 3/5 width, featured */}
                        <div
                            className="lg:col-span-3 flex flex-col rounded-2xl p-8 md:p-10 overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl relative"
                            style={{
                                background: `linear-gradient(135deg, ${accent}, ${hexToRgba(accent, 0.75)})`,
                                opacity: visible ? 1 : 0,
                                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                                transition: 'opacity 0.6s ease, transform 0.6s ease, box-shadow 0.3s ease',
                            }}
                        >
                            <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full opacity-10" style={{ backgroundColor: '#ffffff', filter: 'blur(60px)' }} />
                            {path1.icon && <span className="text-5xl mb-6 block">{path1.icon}</span>}
                            <h3 className="text-3xl font-black text-white mb-3">{path1.title}</h3>
                            {path1.description && <p className="text-white/80 leading-relaxed mb-8 text-base">{path1.description}</p>}
                            {path1.features && path1.features.length > 0 && (
                                <ul className="space-y-3 mb-10 flex-1">
                                    {path1.features.map((feat, i) => (
                                        <li key={i} className="flex items-center gap-3 text-white/90">
                                            <CheckIcon color="rgba(255,255,255,0.9)" />
                                            <span className="text-sm">{feat}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {path1.cta_text && (
                                <a
                                    {...resolveLinkProps(path1.cta_link)}
                                    className="mt-auto inline-flex items-center justify-center gap-2 w-full py-4 rounded-xl font-bold text-base transition-all duration-300 hover:opacity-90 hover:shadow-lg"
                                    style={{ backgroundColor: '#ffffff', color: accent }}
                                >
                                    {path1.cta_text}
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </a>
                            )}
                        </div>

                        {/* Path 2 — 2/5 width */}
                        <div
                            className="lg:col-span-2 flex flex-col rounded-2xl p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl"
                            style={{
                                backgroundColor: s.dark ? 'rgba(255,255,255,0.04)' : '#f9fafb',
                                border: `1.5px solid ${hexToRgba(secondary, 0.25)}`,
                                opacity: visible ? 1 : 0,
                                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                                transition: 'opacity 0.6s ease 150ms, transform 0.6s ease 150ms, box-shadow 0.3s ease',
                            }}
                        >
                            {path2.icon && <span className="text-4xl mb-4 block">{path2.icon}</span>}
                            <h3 className="text-xl font-black mb-2" style={{ color: s.text }}>{path2.title}</h3>
                            {path2.description && <p className="text-sm leading-relaxed mb-6 flex-1" style={{ color: s.textSub }}>{path2.description}</p>}
                            {path2.features && path2.features.length > 0 && (
                                <ul className="space-y-2 mb-8">
                                    {path2.features.map((feat, i) => (
                                        <li key={i} className="flex items-center gap-2 text-sm" style={{ color: s.text }}>
                                            <CheckIcon color={secondary} />
                                            {feat}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {path2.cta_text && (
                                <a
                                    {...resolveLinkProps(path2.cta_link)}
                                    className="mt-auto inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-sm border-2 transition-all duration-300 hover:opacity-90"
                                    style={{ borderColor: secondary, color: secondary, backgroundColor: hexToRgba(secondary, 0.06) }}
                                >
                                    {path2.cta_text}
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Minimal: two buttons with descriptions ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16 items-start">
                    {[
                        { path: path1, color: accent, delay: 0 },
                        { path: path2, color: secondary, delay: 120 },
                    ].map(({ path, color, delay }, idx) => (
                        <div
                            key={idx}
                            className="flex flex-col items-center md:items-start text-center md:text-left gap-4"
                            style={{
                                opacity: visible ? 1 : 0,
                                transform: visible ? 'translateY(0)' : 'translateY(20px)',
                                transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
                            }}
                        >
                            {path.icon && <span className="text-4xl">{path.icon}</span>}
                            <div>
                                <h3 className="text-xl font-bold mb-1" style={{ color: s.text }}>{path.title}</h3>
                                {path.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{path.description}</p>}
                            </div>
                            {path.features && path.features.length > 0 && (
                                <ul className="space-y-1.5 self-stretch">
                                    {path.features.map((feat, i) => (
                                        <li key={i} className="flex items-center gap-2 text-sm" style={{ color: s.text }}>
                                            <CheckIcon color={color} />
                                            {feat}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {path.cta_text && (
                                <a
                                    {...resolveLinkProps(path.cta_link)}
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
                                    style={{ backgroundColor: color, color: '#ffffff', boxShadow: `0 4px 14px ${hexToRgba(color, 0.35)}` }}
                                >
                                    {path.cta_text}
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
